import { useLocation } from "react-router-dom";
import { Button, Chip, Separator, Tooltip } from "@heroui/react";
import { CircleHelp, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { TOUR_EVENT } from "./WelcomeTour";
import { BrandMark } from "./BrandMark";
import { ModeToggle } from "./NavRail";
import { useTheme } from "@/lib/theme";
import { navItemFor } from "./nav";
import { useClock } from "./useClock";
import {
	SESSION_META,
	formatCountdown,
	nextFundingAt,
	nextSessionChangeAt,
	sessionOf,
	utcHm,
} from "../daytrader/dayTrader";

/**
 * Top bar: page title on the left; on the right, the day trader's clock —
 * current session, time to the next session change and to funding, UTC time.
 * On a phone (no rail) it also carries the logo, the Pro switch and the
 * connection dot, and drops the countdowns.
 */
export function TopBar({ phone = false, online = null }: { phone?: boolean; online?: boolean | null }) {
	const { pathname } = useLocation();
	const page = navItemFor(pathname);
	const now = useClock();
	const session = sessionOf(now);
	const change = nextSessionChangeAt(now);
	const funding = nextFundingAt(now);
	const [theme, toggleTheme] = useTheme();

	return (
		<header
			className={cn(
				"sticky top-0 z-30 flex h-14 items-center justify-between border-b border-separator bg-background/85 backdrop-blur",
				phone ? "gap-2 px-3 pt-[env(safe-area-inset-top)]" : "gap-4 px-5",
			)}
		>
			<div className="flex min-w-0 items-center gap-2">
				{phone && <BrandMark collapsed />}
				<div className="min-w-0">
					<div className="truncate text-sm font-semibold">{phone ? page.short : page.label}</div>
					<div className="hidden truncate text-xs text-muted md:block">{page.blurb}</div>
				</div>
			</div>

			<div className={cn("flex shrink-0 items-center text-xs", phone ? "gap-1.5" : "gap-3")}>
				<Chip size="sm" variant="soft" color="accent" className="font-semibold">
					{SESSION_META[session].label}
				</Chip>
				<div className="hidden items-center gap-3 sm:flex">
					<span className="text-muted">
						{SESSION_META[change.to].short} in <span className="num text-foreground">{formatCountdown(change.atSec - now)}</span>
					</span>
					<Separator orientation="vertical" className="h-4" />
					<span className="text-muted">
						Funding <span className="num text-foreground">{formatCountdown(funding - now)}</span>
					</span>
					<Separator orientation="vertical" className="h-4" />
				</div>
				<span className="num flex items-center gap-1.5 text-foreground">
					{phone && (
						<span
							role="status"
							title={online == null ? "Connecting" : online ? "Online" : "Offline: the backend isn't answering"}
							aria-label={online == null ? "Connecting" : online ? "Online" : "Offline"}
							className={cn("size-1.5 rounded-full", online == null ? "bg-warning" : online ? "bg-profit" : "bg-loss")}
						/>
					)}
					{utcHm(now)}
					{!phone && " UTC"}
				</span>
				{phone && <span data-tour="mode"><ModeToggle tooltipPlacement="bottom" /></span>}
				<Tooltip delay={300}>
					<Button
						isIconOnly
						size="sm"
						variant="ghost"
						aria-label="Take the 1-minute tour"
						onPress={() => window.dispatchEvent(new Event(TOUR_EVENT))}
					>
						<CircleHelp className="size-4" />
					</Button>
					<Tooltip.Content>Take the 1-minute tour</Tooltip.Content>
				</Tooltip>
				<Button
					isIconOnly
					size="sm"
					variant="ghost"
					aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
					onPress={toggleTheme}
				>
					{theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
				</Button>
			</div>
		</header>
	);
}
